import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import TaskCard from "./TaskCard";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Plus, MoreHorizontal, Trash2, Pencil, Check } from "lucide-react";

const COLUMN_COLORS = ["#8E8E93", "#007AFF", "#AF52DE", "#30B0C7", "#34C759", "#FF9500", "#FF2D55"];

export const KanbanBoard = ({ board, visibleIds, reload, onTaskClick }) => {
  const { columns } = board;
  const [localTasks, setLocalTasks] = useState(board.tasks);
  useEffect(() => { setLocalTasks(board.tasks); }, [board.tasks]);
  const tasks = localTasks;
  const [dragOver, setDragOver] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [quickAdd, setQuickAdd] = useState(null);
  const [quickTitle, setQuickTitle] = useState("");
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColName, setNewColName] = useState("");
  const [editingCol, setEditingCol] = useState(null);
  const [editColName, setEditColName] = useState("");

  const onDrop = async (e, columnId) => {
    e.preventDefault();
    const indicator = dropIndex;
    setDragOver(null);
    setDropIndex(null);
    const taskId = e.dataTransfer.getData("taskId");
    if (!taskId) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;
    const sorted = tasks.filter((t) => t.column_id === columnId).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const current = sorted.map((t) => t.id);
    const rest = sorted.filter((t) => t.id !== taskId);
    let idx = rest.length;
    if (indicator && indicator.colId === columnId) {
      const visible = sorted.filter((t) => !visibleIds || visibleIds.has(t.id));
      const visibleRest = visible.filter((t) => t.id !== taskId);
      let vi = indicator.index;
      const origVi = visible.findIndex((t) => t.id === taskId);
      if (origVi !== -1 && origVi < vi) vi -= 1;
      vi = Math.max(0, Math.min(vi, visibleRest.length));
      idx = vi < visibleRest.length ? rest.indexOf(visibleRest[vi]) : rest.length;
    }
    idx = Math.max(0, Math.min(idx, rest.length));
    const ids = rest.map((t) => t.id);
    ids.splice(idx, 0, taskId);
    if (task.column_id === columnId && JSON.stringify(ids) === JSON.stringify(current)) return;
    setLocalTasks((prev) => prev.map((t) => {
      if (t.id === taskId) return { ...t, column_id: columnId, order: ids.indexOf(t.id) };
      if (ids.includes(t.id)) return { ...t, order: ids.indexOf(t.id) };
      return t;
    }));
    try {
      await api.post(`/projects/${board.project.id}/tasks/reorder`, { column_id: columnId, task_ids: ids });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    reload();
  };

  const deleteTask = async (task) => {
    if (!window.confirm("Task silinsin mi?")) return;
    setLocalTasks((prev) => prev.filter((t) => t.id !== task.id));
    await api.delete(`/tasks/${task.id}`);
    reload();
  };

  const createTask = async (e, columnId) => {
    e.preventDefault();
    if (!quickTitle.trim()) return;
    await api.post(`/projects/${board.project.id}/tasks`, { title: quickTitle, column_id: columnId });
    setQuickTitle("");
    setQuickAdd(null);
    reload();
  };

  const addColumn = async (e) => {
    e.preventDefault();
    if (!newColName.trim()) return;
    const color = COLUMN_COLORS[columns.length % COLUMN_COLORS.length];
    await api.post(`/projects/${board.project.id}/columns`, { name: newColName, color });
    setNewColName("");
    setAddingColumn(false);
    reload();
  };

  const renameColumn = async (colId) => {
    if (editColName.trim()) {
      await api.patch(`/columns/${colId}`, { name: editColName });
      reload();
    }
    setEditingCol(null);
  };

  const deleteColumn = async (col) => {
    try {
      await api.delete(`/columns/${col.id}`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setColColor = async (colId, color) => {
    await api.patch(`/columns/${colId}`, { color });
    reload();
  };

  return (
    <div data-testid="kanban-board-container" className="flex-1 flex gap-6 overflow-x-auto items-start pb-4 snap-x px-1">
      {columns.map((col) => {
        const allColTasks = tasks.filter((t) => t.column_id === col.id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const colTasks = visibleIds ? allColTasks.filter((t) => visibleIds.has(t.id)) : allColTasks;
        const hiddenCount = allColTasks.length - colTasks.length;
        return (
          <div key={col.id}
            onDragOver={(e) => { e.preventDefault(); setDragOver(col.id); }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => onDrop(e, col.id)}
            className="w-[340px] flex-shrink-0 snap-start flex flex-col max-h-[calc(100vh-150px)]">
            <div data-testid="kanban-column-header" className="flex items-center gap-2 px-1.5 mb-3">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: col.color }} />
              {editingCol === col.id ? (
                <form onSubmit={(e) => { e.preventDefault(); renameColumn(col.id); }} className="flex-1 flex items-center gap-1">
                  <input autoFocus value={editColName} onChange={(e) => setEditColName(e.target.value)}
                    onBlur={() => renameColumn(col.id)} data-testid="kanban-column-rename-input"
                    className="flex-1 h-7 px-2 rounded-lg bg-white border border-slate-300 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#007AFF]/40" />
                  <button type="submit" className="text-[#34C759]"><Check size={14} /></button>
                </form>
              ) : (
                <>
                  <h3 className="text-sm font-bold text-slate-900 dark:text-white tracking-tight">{col.name}</h3>
                  <span data-testid="kanban-column-count" className="text-xs font-semibold text-slate-500 dark:text-slate-400 bg-slate-200/80 dark:bg-white/10 rounded-full px-2 py-0.5">
                    {hiddenCount > 0 ? `${colTasks.length}/${allColTasks.length}` : colTasks.length}
                  </span>
                  <div className="ml-auto flex items-center gap-0.5">
                    <button data-testid="kanban-card-quick-add-button" onClick={() => { setQuickAdd(col.id); setQuickTitle(""); }}
                      className="w-7 h-7 rounded-lg hover:bg-slate-200/70 dark:hover:bg-white/10 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 flex items-center justify-center transition-colors">
                      <Plus size={15} />
                    </button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid="kanban-column-menu-button" className="w-7 h-7 rounded-lg hover:bg-slate-200/70 dark:hover:bg-white/10 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 flex items-center justify-center transition-colors">
                          <MoreHorizontal size={15} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl">
                        <DropdownMenuItem data-testid="kanban-column-rename-item" onClick={() => { setEditingCol(col.id); setEditColName(col.name); }} className="rounded-lg">
                          <Pencil size={13} className="mr-1.5" /> Yeniden adlandır
                        </DropdownMenuItem>
                        <div className="flex items-center gap-1.5 px-2 py-2">
                          {COLUMN_COLORS.map((c) => (
                            <button key={c} data-testid={`kanban-column-color-${c.replace("#", "")}`} onClick={() => setColColor(col.id, c)}
                              className={`w-5 h-5 rounded-full transition-transform hover:scale-110 ${col.color === c ? "ring-2 ring-offset-1 ring-slate-400" : ""}`}
                              style={{ background: c }} />
                          ))}
                        </div>
                        <DropdownMenuItem data-testid="kanban-column-delete-item" onClick={() => deleteColumn(col)} className="rounded-lg text-[#FF2D55]">
                          <Trash2 size={13} className="mr-1.5" /> Kolonu sil
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </>
              )}
            </div>

            <div className={`flex-1 overflow-y-auto flex flex-col gap-4 rounded-2xl p-1.5 -mx-1.5 transition-all ${dragOver === col.id ? "bg-[#007AFF]/5 ring-2 ring-[#007AFF]/25" : ""}`}>
              {quickAdd === col.id && (
                <motion.form initial={{ opacity: 0 }} animate={{ opacity: 1 }} onSubmit={(e) => createTask(e, col.id)}>
                  <input autoFocus data-testid="kanban-quick-add-input" value={quickTitle}
                    onChange={(e) => setQuickTitle(e.target.value)}
                    onBlur={() => { if (!quickTitle.trim()) setQuickAdd(null); }}
                    onKeyDown={(e) => e.key === "Escape" && setQuickAdd(null)}
                    placeholder="Ne yapılması gerekiyor?"
                    className="w-full p-3 text-sm rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#232326] text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-[#007AFF] shadow-sm transition-all placeholder:text-slate-400" />
                </motion.form>
              )}
              <AnimatePresence initial={false}>
                {colTasks.map((task, i) => (
                  <motion.div key={task.id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2 }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const before = e.clientY < rect.top + rect.height / 2;
                      setDropIndex({ colId: col.id, index: before ? i : i + 1 });
                      setDragOver(col.id);
                    }}
                    className="relative">
                    {dropIndex?.colId === col.id && dropIndex.index === i && (
                      <span data-testid="kanban-drop-indicator" className="absolute -top-2.5 left-1 right-1 h-1 rounded-full bg-[#007AFF] shadow-[0_0_8px_rgba(0,122,255,0.5)] z-10" />
                    )}
                    <TaskCard task={task} board={board} color={col.color} onClick={() => onTaskClick(task)} onDelete={deleteTask} />
                    {dropIndex?.colId === col.id && dropIndex.index === i + 1 && i === colTasks.length - 1 && (
                      <span data-testid="kanban-drop-indicator" className="absolute -bottom-2.5 left-1 right-1 h-1 rounded-full bg-[#007AFF] shadow-[0_0_8px_rgba(0,122,255,0.5)] z-10" />
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
              {colTasks.length === 0 && quickAdd !== col.id && (
                <div data-testid="kanban-column-empty" className="h-24 rounded-2xl border-2 border-dashed border-slate-200 dark:border-white/10 flex items-center justify-center text-sm text-slate-400 text-center px-4">
                  {hiddenCount > 0 ? `${hiddenCount} task filtreyle gizlendi` : "Kartları buraya bırakın"}
                </div>
              )}
              {quickAdd !== col.id && (
                <button data-testid="kanban-column-bottom-add-button" onClick={() => { setQuickAdd(col.id); setQuickTitle(""); }}
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-xl border-2 border-dashed border-slate-200 dark:border-white/10 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-white/20 transition-colors text-sm font-semibold shrink-0">
                  <Plus size={16} /> Task Ekle
                </button>
              )}
            </div>
          </div>
        );
      })}

      <div className="w-[300px] flex-shrink-0">
        {addingColumn ? (
          <form onSubmit={addColumn}>
            <input autoFocus data-testid="kanban-new-column-input" value={newColName}
              onChange={(e) => setNewColName(e.target.value)}
              onBlur={() => { if (!newColName.trim()) setAddingColumn(false); }}
              onKeyDown={(e) => e.key === "Escape" && setAddingColumn(false)}
              placeholder="Kolon adı (örn: Review)"
              className="w-full p-3 text-sm font-medium rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-[#232326] dark:text-white outline-none focus:ring-2 focus:ring-[#007AFF] shadow-sm transition-all placeholder:text-slate-400" />
          </form>
        ) : (
          <button data-testid="kanban-column-add-button" onClick={() => setAddingColumn(true)}
            className="w-full rounded-2xl border-2 border-dashed border-slate-200 dark:border-white/10 py-3.5 text-sm font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-white/20 hover:bg-white/50 dark:hover:bg-white/5 transition-all flex items-center justify-center gap-2">
            <Plus size={16} /> Yeni Kolon
          </button>
        )}
      </div>
    </div>
  );
};

export default KanbanBoard;
